import React from 'react';
import { Button } from '@/components/ui/button';
import { DropdownButton } from '@/components/ui/dropdown-button';
import { X, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import OneVerseLogo from '@/components/OneVerseLogo';

interface FullPageHeaderProps {
  title: string;
  onClose?: () => void;
  showDownload?: boolean;
  showCurrentDownload?: boolean;
  showDone?: boolean;
  onDone?: () => void;
  isDoneLoading?: boolean;
  isDoneDisabled?: boolean;
  doneLabel?: string;
  doneError?: string | null;
}

const FullPageHeader: React.FC<FullPageHeaderProps> = ({
  title,
  onClose,
  showDownload,
  showCurrentDownload = true,
  showDone,
  onDone,
  isDoneLoading,
  isDoneDisabled,
  doneLabel = 'Done',
  doneError,
}) => {
  const downloadOptions = [
    ...(showCurrentDownload
      ? [
          {
            label: 'Current',
            value: 'form',
            onClick: () => {
              window.print();
            },
          },
        ]
      : []),
    {
      label: 'Blank Form',
      value: 'blank-form',
      onClick: () => {
        const url = new URL(window.location.href);

        url.searchParams.set('view', 'print');
        url.searchParams.set('print_template', 'true');

        window.open(url.toString(), '_blank');
      },
    },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200 px-8 py-6 print:hidden">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/dashboard">
            <OneVerseLogo className="h-4 w-32" />
          </Link>
          <div className="text-gray-300">|</div>
          <h1 className="text-gray-900 font-normal">{title}</h1>
        </div>
        <div className="flex items-center space-x-4">
          {showDownload && (
            <DropdownButton
              options={downloadOptions}
              variant="outline"
              size="sm"
              className="shadow-none bg-white hover:bg-gray-50"
              dropdownClassName="min-w-[160px]"
            >
              <Download className="h-4 w-4" />
              Download Submission
            </DropdownButton>
          )}
          {showDone && onDone && (
            <div className="flex items-center gap-2">
              {doneError && (
                <span className="text-xs text-red-600">{doneError}</span>
              )}
              <Button
                type="button"
                size="sm"
                onClick={onDone}
                disabled={isDoneLoading || isDoneDisabled}
              >
                {isDoneLoading ? 'Processing...' : doneLabel}
              </Button>
            </div>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              aria-label="Close"
              className="h-8 w-8 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default FullPageHeader;
